import { ArrowRightIcon, CheckIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  FormAnswerBlock,
  FormAnswerItem,
  FormResponseDetail,
  FormsService,
} from "client/openapi";
import { DialogContent } from "components/Dialog";
import PersonPill, { PillColor } from "components/PersonPill";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { errorText } from "./formsApi";
import "./ResponseDialog.css";

export function formatDate(value?: string | null) {
  return value ? moment(value).format("MMM D, YYYY") : "";
}

export function formatDateTime(value?: string | null) {
  return value ? moment(value).format("MMM D, YYYY [at] h:mm a") : "";
}

/** The role colors the forms lists use for people. */
const ROLE_COLOR: Record<string, PillColor> = {
  [Auth0AccountRole.PARENT]: "blue",
  [Auth0AccountRole.ME]: "green",
  [Auth0AccountRole.ORG_TUTOR]: "gold",
};

const SECTION_NAME: Record<string, string> = {
  [FormAnswerBlock.section.PARENT]: "Parent",
  [FormAnswerBlock.section.STUDENT]: "Student",
  [FormAnswerBlock.section.TUTOR]: "Tutor",
};

function Value({ text }: { text: string }) {
  return text ? (
    <span className="whitespace-pre-line">{text}</span>
  ) : (
    <span className="text-gray-400">No answer</span>
  );
}

/**
 * One answer: its label, then the value (an old value struck through
 * first). A new person's answers are all new, so they carry no Added tag.
 */
function Answer({
  item,
  newPerson,
}: {
  item: FormAnswerItem;
  newPerson: boolean;
}) {
  if (item.kind === "note") {
    // A note question: the answer in full, with its line breaks.
    return (
      <div className="response-dialog__answer response-dialog__answer--note">
        <p className="text-sm text-gray-500">{item.label}</p>
        <p className="mt-1.5 rounded-lg bg-gray-50 px-3 py-2 text-sm leading-6 text-gray-900">
          <Value text={item.value} />
        </p>
      </div>
    );
  }
  if (item.new_values?.length && item.kept_values) {
    // Subjects added to ones on file: the whole set, the new ones marked.
    const kept = item.kept_values;
    return (
      <div className="response-dialog__answer">
        <p className="text-sm text-gray-500">{item.label}</p>
        <div className="text-sm text-gray-900">
          {kept.join(", ")}
          {item.new_values.map((name, i) => (
            <Fragment key={name}>
              {kept.length > 0 || i > 0 ? ", " : ""}
              <span className="font-semibold text-[#07854b]">+ {name}</span>
            </Fragment>
          ))}
          {item.source === "form" && (
            <span className="ml-2">
              <PersonPill label="Set by form" color="gray" />
            </span>
          )}
        </div>
      </div>
    );
  }
  const changed = item.result === FormAnswerItem.result.CHANGED;
  return (
    <div className="response-dialog__answer">
      <p className="text-sm text-gray-500">{item.label}</p>
      <div className="text-sm text-gray-900">
        {changed && item.previous && (
          <>
            <span className="text-gray-400 line-through">
              <Value text={item.previous} />
            </span>
            <ArrowRightIcon
              className="mx-1.5 inline align-[-2px] text-gray-400"
              aria-label="changed to"
            />
          </>
        )}
        <Value text={item.value} />
        {item.source === "form" && (
          <span className="ml-2">
            <PersonPill label="Set by form" color="gray" />
          </span>
        )}
        {item.result === FormAnswerItem.result.ADDED &&
          !newPerson &&
          item.source !== "form" && (
            <span className="ml-2">
              <PersonPill label="Added" color="green" />
            </span>
          )}
      </div>
    </div>
  );
}

/** One person's answers, in a card of their own. */
function Section({ block }: { block: FormAnswerBlock }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-gray-100 px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {SECTION_NAME[block.section]}
        </span>
        <h3 className="text-base font-semibold text-gray-900">{block.title}</h3>
        {block.created && (
          <PersonPill
            label={
              block.section === FormAnswerBlock.section.PARENT
                ? "New parent"
                : "New student"
            }
            color="green"
          />
        )}
      </header>
      <div className="px-4 py-1">
        {block.items.length === 0 ? (
          <p className="py-3 text-sm text-gray-500">No questions answered.</p>
        ) : (
          block.items.map((item, i) => (
            <Answer key={i} item={item} newPerson={block.created ?? false} />
          ))
        )}
      </div>
    </section>
  );
}

/** One filled-out form: who and when, then each person's answers. */
export default function ResponseDialog({ responseId }: { responseId: number }) {
  const [detail, setDetail] = useState<FormResponseDetail>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    FormsService.getFormResponse({ responseId })
      .then(setDetail)
      .catch((e) =>
        setError(errorText(e, "These answers could not be loaded."))
      );
  }, [responseId]);

  return (
    <DialogContent className="dialog-content response-dialog">
      <div className="response-dialog__header">
        {detail ? (
          <>
            <h2 className="pr-8 text-xl font-semibold text-gray-900">
              {detail.form_name}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-gray-600">
              <PersonPill
                label={detail.person_name}
                color={ROLE_COLOR[detail.person_type] ?? "gray"}
              />
              <span>Filled out {formatDateTime(detail.submitted_at)}</span>
              {detail.sent_by_name && (
                <span>
                  {detail.sent_by_name === "Website"
                    ? "From your website"
                    : `Sent by ${detail.sent_by_name}`}
                </span>
              )}
            </div>
            {(detail.terms_agreed_at || detail.payment_method_label) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {detail.terms_agreed_at && (
                  <span className="response-dialog__fact">
                    <CheckIcon className="text-[#07854b]" />
                    Terms agreed {formatDateTime(detail.terms_agreed_at)}
                  </span>
                )}
                {detail.payment_method_label && (
                  <span className="response-dialog__fact">
                    {detail.payment_method_label} saved
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="animate-pulse space-y-3 py-1">
            <div className="h-6 w-1/2 rounded bg-gray-200" />
            <div className="h-4 w-2/3 rounded bg-gray-100" />
          </div>
        )}
      </div>

      <div className="response-dialog__body">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {detail && (
          <div className="space-y-4">
            {detail.blocks.map((block, index) => (
              <Section key={index} block={block} />
            ))}
            {detail.terms_agreed_at && detail.terms_text && (
              <details className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <summary className="cursor-pointer text-sm font-medium text-gray-700">
                  The terms as agreed
                </summary>
                <p className="mt-2 max-h-48 overflow-y-auto whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                  {detail.terms_text}
                </p>
              </details>
            )}
          </div>
        )}
      </div>
    </DialogContent>
  );
}
